import { notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { GraphQLService } from '@affine/core/modules/cloud';
import {
  enableTotpMutation,
  totpStatusQuery,
  verifyTotpSetupMutation,
} from '@affine/graphql';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type SetupStep =
  | 'loading'
  | 'password'
  | 'qrcode'
  | 'verify'
  | 'backup-codes'
  | 'already-enabled';

export interface UseTotpSetupReturn {
  step: SetupStep;
  /** Password field — used only in the password-based flow (account settings) */
  password: string;
  setPassword: (v: string) => void;
  verifyCode: string;
  setVerifyCode: (v: string) => void;
  qrCodeDataUrl: string;
  secretKey: string;
  backupCodes: string[];
  isLoading: boolean;
  error: string;
  /** Password-based setup start (account settings dialog) */
  enableTotp: () => void;
  /** Passwordless setup start (onboarding wizard) */
  initSetup: () => void;
  verifySetup: (codeOverride?: string) => void;
  advanceFromQrCode: () => void;
  reset: () => void;
  copyBackupCodes: () => void;
  downloadBackupCodes: () => void;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useTotpSetup(options?: {
  autoInit?: boolean;
}): UseTotpSetupReturn {
  const gql = useService(GraphQLService);
  const [step, setStep] = useState<SetupStep>('password');
  const [password, setPassword] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Guard against React StrictMode double-calling initSetup via useEffect
  const initCalledRef = useRef(false);

  const reset = useCallback(() => {
    setStep('password');
    setPassword('');
    setQrCodeDataUrl('');
    setSecretKey('');
    setVerifyCode('');
    setBackupCodes([]);
    setError('');
    initCalledRef.current = false;
  }, []);

  // Password-based flow (account settings)
  const enableTotp = useAsyncCallback(async () => {
    if (!password) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await gql.gql({
        query: enableTotpMutation,
        variables: { password },
      });
      setQrCodeDataUrl(res.enableTOTP.qrCodeDataUrl);
      setSecretKey(res.enableTOTP.secretKey);
      setStep('qrcode');
    } catch {
      setError('Incorrect password');
    } finally {
      setIsLoading(false);
    }
  }, [gql, password]);

  // Passwordless flow (onboarding wizard)
  const initSetup = useAsyncCallback(async () => {
    // Prevent double-call from React StrictMode (useEffect fires twice in dev)
    if (initCalledRef.current) return;
    initCalledRef.current = true;

    setIsLoading(true);
    setError('');
    try {
      // Check if TOTP is already enabled — skip setup if so
      const status = await gql.gql({ query: totpStatusQuery });
      if (status.totpStatus.enabled) {
        setStep('already-enabled');
        return;
      }

      const res = await gql.gql({
        query: enableTotpMutation,
        variables: {},
      });
      setQrCodeDataUrl(res.enableTOTP.qrCodeDataUrl);
      setSecretKey(res.enableTOTP.secretKey);
      setStep('qrcode');
    } catch {
      // On any failure, move to already-enabled so the user can continue
      setStep('already-enabled');
      setError('Failed to initialize 2FA setup');
    } finally {
      setIsLoading(false);
    }
  }, [gql]);

  // Auto-init on mount when autoInit is true
  useEffect(() => {
    if (options?.autoInit) {
      void initSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifySetup = useAsyncCallback(
    async (codeOverride?: string) => {
      const code = codeOverride ?? verifyCode;
      if (code.length !== 6) return;
      setIsLoading(true);
      setError('');
      try {
        const res = await gql.gql({
          query: verifyTotpSetupMutation,
          variables: { code },
        });
        setBackupCodes(res.verifyTOTPSetup);
        setStep('backup-codes');
      } catch {
        setError('Invalid code. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [gql, verifyCode]
  );

  const advanceFromQrCode = useCallback(() => {
    setStep('verify');
  }, []);

  const copyBackupCodes = useCallback(() => {
    navigator.clipboard
      .writeText(backupCodes.join('\n'))
      .then(() => notify.success({ title: 'Backup codes copied' }))
      .catch(() => notify.error({ title: 'Failed to copy' }));
  }, [backupCodes]);

  const downloadBackupCodes = useCallback(() => {
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'streamlain-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [backupCodes]);

  return {
    step,
    password,
    setPassword,
    verifyCode,
    setVerifyCode,
    qrCodeDataUrl,
    secretKey,
    backupCodes,
    isLoading,
    error,
    enableTotp,
    initSetup,
    verifySetup,
    advanceFromQrCode,
    reset,
    copyBackupCodes,
    downloadBackupCodes,
  };
}
